import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import IncomeAPI, { IncomeEntry, IncomeSource } from "../../services/income.service";
import { PaymentMode } from "../../services/expense.service";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sources: IncomeSource[];
  onSourceCreated: (source: IncomeSource) => void;
  editingEntry?: IncomeEntry | null;
}

const PAYMENT_MODES: { value: PaymentMode; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "other", label: "Other" },
];

export const IncomeEntryModal: React.FC<Props> = ({
  isOpen, onClose, onSuccess, sources, onSourceCreated, editingEntry,
}) => {
  const [form, setForm] = useState({
    source_id: "", amount: "", income_date: new Date().toISOString().slice(0, 10),
    counterparty: "", payment_mode: "" as PaymentMode | "", payment_reference: "", notes: "",
  });
  const [addingSource, setAddingSource] = useState(false);
  const [newSourceName, setNewSourceName] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      if (editingEntry) {
        setForm({
          source_id: editingEntry.source_id,
          amount: String(editingEntry.amount_paise / 100),
          income_date: editingEntry.income_date,
          counterparty: editingEntry.counterparty ?? "",
          payment_mode: editingEntry.payment_mode ?? "",
          payment_reference: editingEntry.payment_reference ?? "",
          notes: editingEntry.notes ?? "",
        });
      } else {
        setForm({
          source_id: "", amount: "", income_date: new Date().toISOString().slice(0, 10),
          counterparty: "", payment_mode: "", payment_reference: "", notes: "",
        });
      }
      setReceiptFile(null);
      setErrors({});
      setAddingSource(false);
      setNewSourceName("");
    }
  }, [isOpen, editingEntry]);

  if (!isOpen) return null;

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.source_id) e.source_id = "Source is required";
    const amountNum = parseFloat(form.amount);
    if (!form.amount || isNaN(amountNum) || amountNum <= 0) e.amount = "Enter a valid amount";
    if (!form.income_date) e.income_date = "Date is required";
    return e;
  };

  const handleAddSource = async () => {
    if (!newSourceName.trim()) return;
    try {
      const code = newSourceName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const source = await IncomeAPI.createSource(newSourceName.trim(), code);
      onSourceCreated(source);
      setForm(f => ({ ...f, source_id: source.source_id }));
      setAddingSource(false);
      setNewSourceName("");
      toast.success("Source added");
    } catch (err: any) {
      toast.error(err?.message || "Failed to add source");
    }
  };

  const uploadReceipt = async (incomeId: string) => {
    if (!receiptFile) return;
    try {
      const { upload_url, s3_key } = await IncomeAPI.getReceiptUploadUrl(incomeId);
      if (upload_url) {
        const res = await fetch(upload_url, {
          method: "PUT", body: receiptFile,
          headers: { "Content-Type": receiptFile.type },
        });
        if (!res.ok) throw new Error(`Receipt upload failed: HTTP ${res.status}`);
      }
      await IncomeAPI.confirmReceipt(incomeId, s3_key);
    } catch (err: any) {
      toast.error(err?.message || "Income entry saved, but receipt upload failed");
    }
  };

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        source_id: form.source_id,
        amount_paise: Math.round(parseFloat(form.amount) * 100),
        income_date: form.income_date,
        counterparty: form.counterparty || undefined,
        payment_mode: form.payment_mode || undefined,
        payment_reference: form.payment_reference || undefined,
        notes: form.notes || undefined,
      };
      let entry: IncomeEntry;
      if (editingEntry) {
        entry = await IncomeAPI.update(editingEntry.income_id, payload);
      } else {
        entry = await IncomeAPI.create(payload);
      }
      if (receiptFile) await uploadReceipt(entry.income_id);
      toast.success(editingEntry ? "Income entry updated" : "Income entry recorded");
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Failed to save income entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-slate-900">
            {editingEntry ? "Edit Income Entry" : "Record Income"}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
            {!addingSource ? (
              <div className="flex gap-2">
                <select
                  className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.source_id ? "border-red-400" : "border-gray-200"}`}
                  value={form.source_id}
                  onChange={e => setForm(f => ({ ...f, source_id: e.target.value }))}
                >
                  <option value="">Select source…</option>
                  {sources.map(s => (
                    <option key={s.source_id} value={s.source_id}>{s.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setAddingSource(true)}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 whitespace-nowrap"
                >
                  + Add
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  autoFocus
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="New source name"
                  value={newSourceName}
                  onChange={e => setNewSourceName(e.target.value)}
                />
                <button type="button" onClick={handleAddSource} className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg">Save</button>
                <button type="button" onClick={() => setAddingSource(false)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg">Cancel</button>
              </div>
            )}
            {errors.source_id && <p className="text-red-500 text-xs mt-1">{errors.source_id}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹)</label>
              <input
                type="number" step="0.01" min="0"
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.amount ? "border-red-400" : "border-gray-200"}`}
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              />
              {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <input
                type="date"
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.income_date ? "border-red-400" : "border-gray-200"}`}
                value={form.income_date}
                onChange={e => setForm(f => ({ ...f, income_date: e.target.value }))}
              />
              {errors.income_date && <p className="text-red-500 text-xs mt-1">{errors.income_date}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Counterparty (who paid)</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={form.counterparty}
              onChange={e => setForm(f => ({ ...f, counterparty: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment mode</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.payment_mode}
                onChange={e => setForm(f => ({ ...f, payment_mode: e.target.value as PaymentMode }))}
              >
                <option value="">Select…</option>
                {PAYMENT_MODES.map(pm => <option key={pm.value} value={pm.value}>{pm.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.payment_reference}
                onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Receipt</label>
            {editingEntry?.receipt_s3_key && !receiptFile && (
              <div className="flex items-center gap-3 mb-2 text-sm">
                <span className="text-emerald-600">✓ Receipt already uploaded</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { view_url } = await IncomeAPI.getReceiptViewUrl(editingEntry.income_id);
                      window.open(view_url, "_blank", "noopener,noreferrer");
                    } catch (err: any) {
                      toast.error(err?.message || "Failed to load receipt");
                    }
                  }}
                  className="text-blue-600 hover:underline"
                >
                  View
                </button>
              </div>
            )}
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={e => setReceiptFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-600 file:text-sm"
            />
            {editingEntry?.receipt_s3_key && (
              <p className="text-xs text-slate-400 mt-1">Choosing a new file will replace the existing receipt.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              rows={2}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">
              {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IncomeEntryModal;
